import { FormEvent, useEffect, useRef, useState } from 'react'
import { router, useForm, usePage } from '@inertiajs/react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { reportsMonthlySignPath } from '@/shared/lib/routes'
import { t } from '@/shared/lib/i18n'

const MIN_PIN_LENGTH = 4
const PIN_CHANGE_PATH = '/profile/change_pin'

type Step = 'sign' | 'create-pin'

interface Props {
  reportId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  hasUserPin: boolean
  /** 'show' keeps the user on the report after signing; omitted returns to approvals. */
  returnTo?: 'show'
}

interface PinFormData {
  newPin: string
  confirmPin: string
}

/**
 * Single controlled dialog that drives the whole signing UX from any entry point:
 * confirm-and-sign (State A), create-PIN when none is set (State B), and the
 * create → resume transition. Reads the backend no-PIN safety-net signal from the
 * shared Inertia error `errors.pin_not_set`.
 */
const SignReportDialog = ({ reportId, open, onOpenChange, hasUserPin, returnTo }: Props) => {
  const { errors } = usePage<{ errors?: Record<string, string> }>().props

  const initialStep = (): Step => (hasUserPin ? 'sign' : 'create-pin')

  const [step, setStep] = useState<Step>(initialStep)
  const [signPin, setSignPin] = useState('')
  const [signComment, setSignComment] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [signing, setSigning] = useState(false)
  const [justCreatedPin, setJustCreatedPin] = useState(false)

  const pinForm = useForm<PinFormData>({ newPin: '', confirmPin: '' })
  const pinFieldRef = useRef<HTMLInputElement | null>(null)

  const resetTransientState = () => {
    setStep(initialStep())
    setSignPin('')
    setSignComment('')
    setPinError(null)
    setSigning(false)
    setJustCreatedPin(false)
    pinForm.reset()
    pinForm.clearErrors()
  }

  // Choose the initial step each time the dialog opens.
  useEffect(() => {
    if (open) {
      resetTransientState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Safety net: if shared props were stale and the backend reports the PIN is not
  // set, switch to the create-PIN state instead of showing a generic error.
  useEffect(() => {
    if (open && errors?.pinNotSet) {
      setStep('create-pin')
    }
  }, [open, errors])

  // Move focus into the sign PIN field on the create → resume transition.
  useEffect(() => {
    if (open && step === 'sign') {
      pinFieldRef.current?.focus()
    }
  }, [open, step])

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) resetTransientState()
    onOpenChange(nextOpen)
  }

  const handleSign = () => {
    setPinError(null)
    setSigning(true)
    const payload: Record<string, string | boolean> = { is_signed: true }
    if (returnTo) payload.return_to = returnTo
    if (signComment.trim()) payload.comments = signComment.trim()
    payload.pin = signPin

    router.post(reportsMonthlySignPath({ id: reportId }), payload, {
      preserveScroll: true,
      onSuccess: () => {
        handleClose(false)
      },
      onError: (formErrors) => {
        setJustCreatedPin(false)
        if (formErrors.pinNotSet) {
          setStep('create-pin')
        } else if (formErrors.invalidPin) {
          setPinError(formErrors.invalidPin)
        }
      },
      onFinish: () => setSigning(false),
    })
  }

  const handleCreatePin = (event: FormEvent) => {
    event.preventDefault()
    pinForm.clearErrors()

    if (pinForm.data.newPin !== pinForm.data.confirmPin) {
      pinForm.setError('confirmPin', t('reports.signatures.pinMismatch'))
      return
    }
    if (pinForm.data.newPin.length < MIN_PIN_LENGTH) {
      pinForm.setError('newPin', t('reports.signatures.pinTooShort'))
      return
    }

    pinForm.transform((data) => ({
      pin: { new_pin: data.newPin, confirm_pin: data.confirmPin },
      context: 'signing',
    }))
    pinForm.patch(PIN_CHANGE_PATH, {
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        setSignPin('')
        setSignComment('')
        setPinError(null)
        setJustCreatedPin(true)
        setStep('sign')
        // Refresh only the shared auth prop so hasUserPin reflects the new PIN,
        // without re-fetching the whole page's report data.
        router.reload({ only: ['currentUserInfo'] })
      },
    })
  }

  const pinFormErrors = pinForm.errors as Record<string, string | undefined>

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        {step === 'create-pin' ? (
          <form onSubmit={handleCreatePin}>
            <DialogHeader>
              <DialogTitle>{t('reports.signatures.createPinTitle')}</DialogTitle>
              <DialogDescription>{t('reports.signatures.createPinPrompt')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              <div className="space-y-1">
                <Label htmlFor={`create-new-pin-${reportId}`}>
                  {t('reports.signatures.newPinLabel')}
                </Label>
                <PasswordInput
                  id={`create-new-pin-${reportId}`}
                  autoFocus
                  autoComplete="new-password"
                  value={pinForm.data.newPin}
                  onChange={(event) => pinForm.setData('newPin', event.target.value)}
                />
                <p className="text-xs text-muted-foreground">{t('reports.signatures.pinHint')}</p>
                {pinFormErrors.newPin && (
                  <p className="text-sm text-destructive">{pinFormErrors.newPin}</p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={`create-confirm-pin-${reportId}`}>
                  {t('reports.signatures.confirmPinLabel')}
                </Label>
                <PasswordInput
                  id={`create-confirm-pin-${reportId}`}
                  autoComplete="new-password"
                  value={pinForm.data.confirmPin}
                  onChange={(event) => pinForm.setData('confirmPin', event.target.value)}
                />
                {pinFormErrors.confirmPin && (
                  <p className="text-sm text-destructive">{pinFormErrors.confirmPin}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                {t('reports.signatures.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={
                  pinForm.processing ||
                  !pinForm.data.newPin.trim() ||
                  !pinForm.data.confirmPin.trim()
                }
              >
                {t('reports.signatures.createPinButton')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('reports.signatures.confirmSignTitle')}</DialogTitle>
              <DialogDescription>
                {justCreatedPin
                  ? t('reports.signatures.pinCreatedPrompt')
                  : t('reports.signatures.confirmSign')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 py-4">
              <div className="space-y-1">
                <Label htmlFor={`sign-pin-${reportId}`}>{t('reports.signatures.pinLabel')}</Label>
                <Input
                  id={`sign-pin-${reportId}`}
                  ref={pinFieldRef}
                  type="password"
                  placeholder={t('reports.signatures.pinPlaceholder')}
                  value={signPin}
                  onChange={(event) => setSignPin(event.target.value)}
                  autoComplete="off"
                  aria-describedby={pinError ? `sign-pin-error-${reportId}` : undefined}
                />
                {pinError && (
                  <p id={`sign-pin-error-${reportId}`} className="text-sm text-destructive">
                    {pinError}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor={`sign-comment-${reportId}`}>
                  {t('reports.signatures.commentOptional')}
                </Label>
                <Textarea
                  id={`sign-comment-${reportId}`}
                  value={signComment}
                  onChange={(event) => setSignComment(event.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                {t('reports.signatures.cancel')}
              </Button>
              <Button type="button" onClick={handleSign} disabled={signing || !signPin.trim()}>
                {t('reports.signatures.sign')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SignReportDialog
